import { supabase } from '../lib/supabase'
import type { Deliverable, Project } from '../types/portal'

/**
 * Project and deliverable writes.
 *
 * Everything runs through the authenticated staff session and the project's
 * existing RLS policies, with the publishable key only. A refused write is
 * reported with the table and the exact Postgres message rather than being
 * swallowed — nothing silently falls back to browser-only storage.
 */

export interface WriteResult {
  ok: boolean
  error?: string
}

function fail(table: string, error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `${table}: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] write refused — ${detail}`)
  return { ok: false, error: detail }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Postgres "column does not exist". */
const UNDEFINED_COLUMN = '42703'

/**
 * The database's own project id. A project created moments ago may still be
 * held under a temporary local id, so the slug is used to recover the real
 * UUID rather than writing a foreign key that cannot exist.
 */
export async function resolveProjectId(projectId: string, slug: string): Promise<string | null> {
  if (UUID.test(projectId)) return projectId
  if (!supabase || !slug) return null

  const { data, error } = await supabase
    .from('projects')
    .select('id')
    .eq('slug', slug)
    .maybeSingle()

  if (error) {
    fail('projects', error)
    return null
  }
  return data ? String(data.id) : null
}

/**
 * The review and delivery columns. Held separately so a schema that predates
 * them can still be written to: the row is retried without them and the missing
 * columns are named, rather than the whole save failing or silently succeeding.
 */
const LINK_COLUMNS = [
  'drive_url',
  'audio_url',
  'video_format',
  /* Which source document a transcript came from, and how long it is. */
  'transcript_source_filename',
  'transcript_word_count',
  /* Which film or podcast a transcript belongs to (29 Sep 2026). */
  'transcript_for_key',
] as const

const MISSING_LINK_COLUMNS =
  'deliverables: columns are missing (drive_url, audio_url, video_format, transcript_source_filename, transcript_word_count, transcript_for_key)'

function withoutLinkColumns<T extends Record<string, unknown>>(row: T): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...row }
  for (const column of LINK_COLUMNS) delete copy[column]
  return copy
}

function hasLinkColumns(row: Record<string, unknown>): boolean {
  return LINK_COLUMNS.some((column) => column in row)
}

/** The deliverable columns, mapped from the interface's own field names. */
function deliverableRow(projectId: string, item: Deliverable, sortOrder: number) {
  return {
    project_id: projectId,
    deliverable_key: item.id,
    title: item.name,
    version: item.version,
    category: item.type,
    status: item.status,
    visible_to_client: item.visibleToClient,
    sort_order: sortOrder,
    /* The links a client actually watches and downloads from, and how a video
       is framed — all of which must survive a reload. */
    drive_url: item.driveUrl || null,
    audio_url: item.audioUrl || null,
    video_format: item.videoFormat ?? null,
    /* Transcript source metadata: staff-only, and it must survive a reload. */
    transcript_source_filename: item.transcriptFilename || null,
    transcript_word_count: item.transcriptWordCount ?? null,
    /* The film or podcast this transcript belongs to. Without it the client
       page cannot place the transcript and simply never shows it. */
    transcript_for_key: item.transcriptFor || null,
  }
}

export interface CreatedProject {
  id: string
  accessToken: string
}

/**
 * Inserts the project, reads back its generated id and access token, then
 * inserts any deliverables created with it against that real project id.
 */
export async function insertProject(
  project: Project,
): Promise<{ result: WriteResult; created?: CreatedProject }> {
  if (!supabase) return { result: { ok: false, error: 'Supabase is not configured.' } }

  const { data, error } = await supabase
    .from('projects')
    .insert({
      client_name: project.client,
      project_name: project.name,
      slug: project.slug,
      status: 'active',
      project_type: project.type || null,
      description: project.description || null,
      client_contact: project.clientContact || null,
    })
    .select('id, access_token')
    .single()

  const result = fail('projects', error)
  if (!result.ok || !data) return { result }

  const created: CreatedProject = {
    id: String(data.id),
    accessToken: String(data.access_token ?? ''),
  }

  /* Every deliverable created with the project is stored against the real id. */
  if (project.deliverables.length > 0) {
    const rows = project.deliverables.map((item, index) => deliverableRow(created.id, item, index))
    const { error: deliverableError } = await supabase.from('deliverables').insert(rows)

    if (deliverableError?.code === UNDEFINED_COLUMN) {
      const { error: retryError } = await supabase
        .from('deliverables')
        .insert(rows.map(withoutLinkColumns))
      if (retryError) return { result: fail('deliverables', retryError), created }
      console.error(`[supabase] write refused — ${MISSING_LINK_COLUMNS}`, deliverableError)
      return { result: { ok: false, error: MISSING_LINK_COLUMNS }, created }
    }

    const deliverableResult = fail('deliverables', deliverableError)
    if (!deliverableResult.ok) return { result: deliverableResult, created }
  }

  return { result: { ok: true }, created }
}

const MISSING_CALL_SHEET_COLUMNS =
  'projects: columns are missing (call_sheet_path, call_sheet_file_name)'

/** Project-level fields the schema carries. */
export async function updateProjectRow(
  projectId: string,
  slug: string,
  patch: Partial<Project>,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  /* A project created moments ago may still be held under a temporary local
     id — resolved the same way every sibling write does, rather than
     reporting a false success for a row that was never touched. */
  const realId = await resolveProjectId(projectId, slug)
  if (!realId) {
    const detail = 'projects: the project could not be found in the database'
    console.error(`[supabase] write refused — ${detail}`)
    return { ok: false, error: detail }
  }

  const row: Record<string, unknown> = {}
  if (patch.client !== undefined) row.client_name = patch.client
  if (patch.name !== undefined) row.project_name = patch.name
  if (patch.type !== undefined) row.project_type = patch.type || null
  if (patch.description !== undefined) row.description = patch.description || null
  if (patch.clientContact !== undefined) row.client_contact = patch.clientContact || null
  if (patch.archived !== undefined) row.status = patch.archived ? 'archived' : 'active'
  /* The call sheet itself already lives in Storage by the time this runs —
     this just points the project row at it (or clears the pointer). */
  if (patch.callSheet !== undefined) {
    row.call_sheet_path = patch.callSheet?.path || null
    row.call_sheet_file_name = patch.callSheet?.fileName || null
  }

  /**
   * The shoot no longer lives on the project row.
   *
   * `next_shoot_date` and its two companions are now DERIVED from
   * `project_shoots` by a database trigger, so writing them here would be
   * reverted the next time the mirror is refreshed — a save that appears to
   * work and then quietly undoes itself. The `set_project_next_shoot` RPC
   * edits the shoot day that "next shoot" actually refers to and lets the
   * mirror follow. See utils/projectShoots.ts.
   */
  const touchesShoot =
    patch.nextShootDate !== undefined ||
    patch.nextShootTime !== undefined ||
    patch.location !== undefined

  if (Object.keys(row).length === 0 && !touchesShoot) return { ok: true }

  if (Object.keys(row).length > 0) {
    const { error } = await supabase.from('projects').update(row).eq('id', realId)

    if (error?.code === UNDEFINED_COLUMN && 'call_sheet_path' in row) {
      const rest = { ...row }
      delete rest.call_sheet_path
      delete rest.call_sheet_file_name
      if (Object.keys(rest).length > 0) await supabase.from('projects').update(rest).eq('id', realId)
      console.error(`[supabase] write refused — ${MISSING_CALL_SHEET_COLUMNS}`, error)
      return { ok: false, error: MISSING_CALL_SHEET_COLUMNS }
    }

    const result = fail('projects', error)
    if (!result.ok) return result
  }

  if (touchesShoot) {
    let date = patch.nextShootDate
    let time = patch.nextShootTime
    let location = patch.location

    /* A partial patch must not blank the two fields it did not mention — the
       RPC always writes all three, so the missing ones are read back first. */
    if (date === undefined || time === undefined || location === undefined) {
      const { data } = await supabase
        .from('projects')
        .select('next_shoot_date, next_shoot_time, shoot_location')
        .eq('id', realId)
        .maybeSingle()
      if (date === undefined) date = String(data?.next_shoot_date ?? '').slice(0, 10)
      if (time === undefined) time = String(data?.next_shoot_time ?? '')
      if (location === undefined) location = String(data?.shoot_location ?? '')
    }

    const { error } = await supabase.rpc('set_project_next_shoot', {
      p_project_id: realId,
      p_date: date || null,
      p_time: time || null,
      p_location: location || null,
    })
    const result = fail('project_shoots', error)
    if (!result.ok) return result
  }

  return { ok: true }
}

const DELETE_ERROR = 'Project could not be deleted. Nothing was removed.'

/**
 * Permanently removes one project through a single secure database operation.
 *
 * The work is done by the `delete_project` RPC, which runs as one transaction:
 * it verifies the caller is authorised Portal staff, deletes only the rows
 * belonging to that exact project id (using the schema's own cascades where
 * they exist), and then the project row itself. If any part fails the whole
 * thing is rolled back, so a project can never be left half-deleted — which is
 * why the browser performs no deletes of its own. Deleting the project row
 * takes its access token with it, so the client link stops working.
 *
 * Nothing in Google Drive is touched.
 */
export async function deleteProjectRow(
  projectId: string,
  slug: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) {
    console.error('[supabase] delete_project not called — no database id for this project')
    return { ok: false, error: DELETE_ERROR }
  }

  /* The RPC is the ONLY permanent-delete path. There is deliberately no
     table-level delete and no fallback: a refusal must leave everything. */
  const { error } = await supabase.rpc('delete_project', { p_project_id: realId })
  if (error) {
    // The real cause is logged for debugging; staff see one plain sentence.
    console.error('[supabase] delete_project failed', error)
    return { ok: false, error: DELETE_ERROR }
  }
  return { ok: true }
}

export async function insertDeliverable(
  projectId: string,
  slug: string,
  item: Deliverable,
  sortOrder: number,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) {
    const detail = 'deliverables: the project could not be found in the database'
    console.error(`[supabase] write refused — ${detail}`)
    return { ok: false, error: detail }
  }

  const row = deliverableRow(realId, item, sortOrder)
  const { error } = await supabase.from('deliverables').insert(row)

  if (error?.code === UNDEFINED_COLUMN) {
    const { error: retryError } = await supabase
      .from('deliverables')
      .insert(withoutLinkColumns(row))
    if (retryError) return fail('deliverables', retryError)
    console.error(`[supabase] write refused — ${MISSING_LINK_COLUMNS}`, error)
    return { ok: false, error: MISSING_LINK_COLUMNS }
  }

  return fail('deliverables', error)
}

export async function updateDeliverableRow(
  projectId: string,
  slug: string,
  deliverableKey: string,
  patch: Partial<Deliverable>,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) {
    const detail = 'deliverables: the project could not be found in the database'
    console.error(`[supabase] write refused — ${detail}`)
    return { ok: false, error: detail }
  }

  const row: Record<string, unknown> = {}
  if (patch.name !== undefined) row.title = patch.name
  if (patch.version !== undefined) row.version = patch.version
  if (patch.type !== undefined) row.category = patch.type
  if (patch.status !== undefined) row.status = patch.status
  if (patch.visibleToClient !== undefined) row.visible_to_client = patch.visibleToClient
  /* The Drive links and video framing are what the client's player and files
     read from, so they are stored rather than held for the session. */
  if (patch.driveUrl !== undefined) row.drive_url = patch.driveUrl || null
  if (patch.audioUrl !== undefined) row.audio_url = patch.audioUrl || null
  if (patch.videoFormat !== undefined) row.video_format = patch.videoFormat ?? null
  if (patch.transcriptFilename !== undefined) {
    row.transcript_source_filename = patch.transcriptFilename || null
  }
  if (patch.transcriptWordCount !== undefined) {
    row.transcript_word_count = patch.transcriptWordCount ?? null
  }
  if (patch.transcriptFor !== undefined) {
    row.transcript_for_key = patch.transcriptFor || null
  }
  if (Object.keys(row).length === 0) return { ok: true }

  const write = (values: Record<string, unknown>) =>
    supabase
      .from('deliverables')
      .update(values)
      .eq('project_id', realId)
      .eq('deliverable_key', deliverableKey)

  const { error } = await write(row)

  /* Save whatever the schema can hold, then say plainly which columns are
     missing — never report a false success for a link that was not stored. */
  if (error?.code === UNDEFINED_COLUMN && hasLinkColumns(row)) {
    const rest = withoutLinkColumns(row)
    if (Object.keys(rest).length > 0) await write(rest)
    console.error(`[supabase] write refused — ${MISSING_LINK_COLUMNS}`, error)
    return { ok: false, error: MISSING_LINK_COLUMNS }
  }

  return fail('deliverables', error)
}

export async function deleteDeliverableRow(
  projectId: string,
  deliverableKey: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase
    .from('deliverables')
    .delete()
    .eq('project_id', projectId)
    .eq('deliverable_key', deliverableKey)
  return fail('deliverables', error)
}

/**
 * Persists the studio's chosen order so the client sees the same sequence.
 *
 * Goes through the `set_deliverable_order` RPC — a single `UPDATE ... FROM
 * unnest(...)` that can only ever touch rows that already exist. A plain
 * client-side `.upsert(rows, {onConflict: ...})` was tried first (see git
 * history / the project handoff doc, §8), but Postgres validates NOT NULL
 * columns (title, etc.) on the row being constructed BEFORE it evaluates
 * whether that row conflicts with an existing one — so an upsert that only
 * sends project_id/deliverable_key/sort_order fails with a not-null
 * violation on `title` every time, even though the row genuinely already
 * exists. Doing a real UPDATE instead of an upsert makes that failure mode
 * structurally impossible: nothing here can ever attempt an INSERT.
 */
export async function saveDeliverableOrderRows(
  projectId: string,
  keys: string[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  if (keys.length === 0) return { ok: true }

  const { error } = await supabase.rpc('set_deliverable_order', {
    p_project_id: projectId,
    p_keys: keys,
  })

  return fail('deliverables', error)
}
